import { useCallback, useEffect, useRef, useState } from 'react';
import { AlertCircle, Building2, Lock, RefreshCw } from 'lucide-react';
import { usePayComContext } from '@/lib/paycom/context';
import type { SavedBankInstrument } from '@/lib/paycom/types';
import { Button } from '@/components/ui';
import { PaymentMethodsSkeleton } from '@/components/features/Deposit/PaymentMethodsSkeleton';

interface Props {
  /** Called with the freshly-tokenized US bank account. Caller is responsible
   *  for refreshing the picker's saved-account list (or appending locally). */
  onSaved: (account: SavedBankInstrument) => void;
  onCancel: () => void;
}

// Sits inside a PayComProvider minted with `paymentMethodType:
// 'us_bank_account'`. Drives `checkout.headless('us_bank_account', …)` to
// launch the Plaid Link flow. Pay.com's callback page (`callbacks.pay.com
// /apm/plaid`) is supposed to `postMessage` the result back and close
// itself, but bank-side OAuth strips `window.opener` via Cross-Origin-
// Opener-Policy on many institutions, leaving the popup stranded. We
// recover by polling the Pay.com vault from the parent and showing the
// user a manual "Plaid finished?" panel so they're never stuck.
export function WithdrawalBankSetup({ onSaved, onCancel }: Props) {
  const { state: payCom } = usePayComContext();
  const [phase, setPhase] = useState<'idle' | 'awaiting'>('idle');
  const [error, setError] = useState<string | null>(null);
  const controllerRef = useRef<PlaidLinkController | null>(null);

  // Diagnostic: log what payment methods the SDK reports for this session.
  // If `allowedPaymentMethods` doesn't contain anything bank-shaped, the
  // session was minted with the wrong `payment_method_types` and the
  // `headless()` call will fail no matter which method string we try.
  useEffect(() => {
    if (!payCom.isReady) return;
    console.info('[WithdrawalBankSetup] SDK allowed methods:', payCom.allowedPaymentMethods);
  }, [payCom.isReady, payCom.allowedPaymentMethods]);

  // Stop the polling when the component unmounts (user navigates away).
  useEffect(() => {
    return () => {
      controllerRef.current?.cancel();
    };
  }, []);

  const handleStart = useCallback(() => {
    if (!payCom.checkoutObject) return;
    setError(null);
    setPhase('awaiting');
    controllerRef.current = startPlaidLink(payCom.checkoutObject, {
      onSuccess: (account) => {
        controllerRef.current = null;
        onSaved(account);
      },
      onFailure: (err) => {
        controllerRef.current = null;
        setError(err.message);
        alert(err.message);
        setPhase('idle');
      },
    });
  }, [payCom.checkoutObject, onSaved]);

  const handleCheckNow = useCallback(() => {
    controllerRef.current?.rescan();
  }, []);

  const handleAbort = useCallback(() => {
    controllerRef.current?.cancel();
    controllerRef.current = null;
    console.log('handleAbort');
    setPhase('idle');
    setError(null);
  }, []);

  if (payCom.isInitializing) {
    return <PaymentMethodsSkeleton />;
  }
  if (payCom.isFailed || !payCom.checkoutObject) {
    return (
      <div className="flex flex-col gap-4">
        <div
          role="alert"
          className="flex items-start gap-3 rounded-card border border-danger-alt/30 bg-danger-alt/[0.04] p-4"
        >
          <AlertCircle className="size-5 text-danger-alt shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-primary">Couldn't load the bank flow</p>
            <p className="text-xs text-muted mt-1">{payCom.error ?? 'Try again in a moment.'}</p>
          </div>
        </div>
        <Button type="button" variant="outline" onClick={onCancel}>
          Go back
        </Button>
      </div>
    );
  }

  if (phase === 'awaiting') {
    return (
      <div className="flex flex-col gap-4">
        <div className="flex items-start gap-3 rounded-card border border-accent/30 bg-accent/[0.04] p-4">
          <RefreshCw className="size-5 text-accent shrink-0 mt-0.5 animate-spin [animation-duration:2.5s]" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-primary">Waiting for Plaid to finish</p>
            <p className="text-xs text-muted mt-1">
              Complete the bank login in the Plaid window. We'll bring you back here automatically.
            </p>
            <p className="text-xs text-muted mt-2">
              If the Plaid window says it can't return you here, finish there, close it, and tap{' '}
              <span className="font-semibold text-primary">"I finished linking"</span> below.
            </p>
          </div>
        </div>

        {error && (
          <div
            role="alert"
            aria-live="polite"
            className="flex items-start gap-2 rounded-card border border-danger-alt/30 bg-danger-alt/[0.04] px-3 py-2.5"
          >
            <AlertCircle className="size-4 text-danger-alt shrink-0 mt-0.5" />
            <p className="text-sm text-danger-alt">{error}</p>
          </div>
        )}

        <div className="flex flex-col gap-2">
          <Button type="button" size="lg" onClick={handleCheckNow}>
            I finished linking
          </Button>
          <Button type="button" variant="outline" onClick={handleAbort}>
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start gap-3 rounded-card border border-border bg-surface p-4">
        <Building2 className="size-5 text-accent shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-primary">Link your US bank account</p>
          <p className="text-xs text-muted mt-1">
            A Plaid window opens to sign in to your bank and choose an account. Pay.com vaults the connection so you only
            need to do this once.
          </p>
        </div>
      </div>

      {error && (
        <div
          role="alert"
          aria-live="polite"
          className="flex items-start gap-2 rounded-card border border-danger-alt/30 bg-danger-alt/[0.04] px-3 py-2.5"
        >
          <AlertCircle className="size-4 text-danger-alt shrink-0 mt-0.5" />
          <p className="text-sm text-danger-alt">{error}</p>
        </div>
      )}

      <div className="flex flex-col gap-2">
        <Button type="button" size="lg" onClick={handleStart}>
          Continue with Plaid
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>

      <p className="flex items-center justify-center gap-1.5 text-[10px] uppercase tracking-[0.12em] text-muted pt-1">
        <Lock className="size-3" />
        Secured by Plaid · Pay.com vault
      </p>
    </div>
  );
}

// Polling cadence — fast enough to feel responsive, slow enough to not
// hammer Pay.com's API. The window-focus listener catches the user
// returning faster than the next tick anyway.
const POLL_INTERVAL_MS = 2_000;
// Auto-abort if neither SETUP_SUCCESS, headless().then, nor the polling
// fires within this window. Long enough to cover most Plaid sessions.
const SETUP_TIMEOUT_MS = 5 * 60 * 1_000;

interface PlaidLinkCallbacks {
  onSuccess: (account: SavedBankInstrument) => void;
  onFailure: (err: Error) => void;
}

interface PlaidLinkController {
  /** Manually trigger a vault rescan — used by the "I finished linking"
   *  button when the popup is stuck and can't `postMessage` back. */
  rescan: () => void;
  /** Abort the flow, clear all listeners and timers. */
  cancel: () => void;
}

// Drives the SDK headless bank-link flow. Triple-redundant success
// detection:
//   1. SETUP_SUCCESS event (works when window.opener survives bank OAuth)
//   2. headless() promise resolution (same caveat)
//   3. Vault polling + window-focus rescan + manual user click (works
//      even when Pay.com's callback page can't postMessage back due to
//      Cross-Origin-Opener-Policy on the bank's site — common path)
function startPlaidLink(
  checkout: NonNullable<ReturnType<typeof usePayComContext>['state']['checkoutObject']>,
  callbacks: PlaidLinkCallbacks,
): PlaidLinkController {
  let settled = false;
  let pollTimerId: number | null = null;
  let timeoutId: number | null = null;
  let preflightSourceIds = new Set<string>();

  const settleSuccess = (account: SavedBankInstrument) => {
    if (settled) return;
    settled = true;
    cleanup();
    callbacks.onSuccess(account);
  };
  const settleFailure = (err: Error) => {
    console.log('settleFailure', JSON.stringify(err, null, 2));
    if (settled) return;
    settled = true;
    cleanup();
    callbacks.onFailure(err);
  };

  // Look up the just-linked source in Pay.com's vault. Prefer one that
  // wasn't in the preflight snapshot; fall back to any us_bank_account if
  // the diff comes up empty (e.g. preflight failed).
  const tryPickNewBank = async (): Promise<SavedBankInstrument | null> => {
    const sources = await checkout.getExistingSources();
    const newBank = sources.find(
      (s) => (s as unknown as { type: string }).type === 'us_bank_account' && !preflightSourceIds.has(s.id),
    );
    const anyBank = newBank ?? sources.find((s) => (s as unknown as { type: string }).type === 'us_bank_account');
    if (!anyBank) return null;
    return extractBankInstrument([anyBank]);
  };

  const pollOnce = async () => {
    if (settled) return;
    try {
      const account = await tryPickNewBank();
      if (account) settleSuccess(account);
    } catch (err) {
      console.warn('Pay.com vault poll failed (transient):', err);
    }
  };

  const onFocus = () => {
    void pollOnce();
  };

  const onSuccess = (...args: unknown[]) => {
    console.info('Pay.com SETUP_SUCCESS:', args);
    const direct = extractBankInstrument(args);
    if (direct) {
      settleSuccess(direct);
      return;
    }
    void pollOnce();
  };
  const onFailure = (...args: unknown[]) => {
    console.error('Pay.com SETUP_FAILURE:', args);
    settleFailure(new Error(extractFailureMessage(args) ?? 'Plaid did not complete.'));
  };

  function cleanup() {
    if (timeoutId !== null) {
      window.clearTimeout(timeoutId);
      timeoutId = null;
    }
    if (pollTimerId !== null) {
      window.clearInterval(pollTimerId);
      pollTimerId = null;
    }
    window.removeEventListener('focus', onFocus);
    try {
      checkout.removeListener(checkout.EVENT_TYPES.SETUP_SUCCESS, onSuccess);
      checkout.removeListener(checkout.EVENT_TYPES.SETUP_FAILURE, onFailure);
    } catch {
      // Listener may not be registered yet; safe to ignore.
    }
  }

  // Snapshot pre-flight, then kick off all detection paths in parallel.
  (async () => {
    try {
      const preSources = await checkout.getExistingSources();
      preflightSourceIds = new Set(preSources.map((s) => s.id));
    } catch (err) {
      console.warn('Pay.com preflight getExistingSources failed; rescan diff will be permissive:', err);
    }

    if (settled) return;

    timeoutId = window.setTimeout(
      () => settleFailure(new Error('Bank linking timed out. Please try again.')),
      SETUP_TIMEOUT_MS,
    );
    pollTimerId = window.setInterval(pollOnce, POLL_INTERVAL_MS);
    window.addEventListener('focus', onFocus);

    checkout.once(checkout.EVENT_TYPES.SETUP_SUCCESS, onSuccess);
    checkout.once(checkout.EVENT_TYPES.SETUP_FAILURE, onFailure);

    checkout
      .headless('us_bank_account', undefined, undefined, { saveSourceForFutureUse: true })
      .then(async (result) => {
        console.info('Pay.com headless(us_bank_account) resolved:', result);
        console.log('result', JSON.stringify(result, null, 2));
      })
      .catch((err: unknown) => {
        console.error("Pay.com headless('us_bank_account') threw:", err);
        alert(JSON.stringify(err, null, 2));
      });
  })();

  return {
    rescan: () => void pollOnce(),
    cancel: () => settleFailure(new Error('Cancelled')),
  };
}

// Pay.com surfaces bank metadata under `us_bank_account` on the source row.
// We look across the most likely shapes so this works whether the SDK
// returns the source directly, wraps it under `paymentMethod`, or emits it
// inside a SETUP_SUCCESS event payload.
function extractBankInstrument(args: unknown[]): SavedBankInstrument | null {
  for (const arg of args) {
    if (!arg) continue;
    if (typeof arg === 'string' && arg.startsWith('pm_us_bank_account_')) {
      return { id: arg, last4: '', bankName: '', accountType: 'checking', accountHolderName: null };
    }
    if (typeof arg !== 'object') continue;
    const obj = arg as Record<string, unknown>;
    const id = pickBankTokenId(obj);
    if (!id) continue;
    // Pay.com's `getExistingSources()` returns `{ bankAccount: {...} }` with
    // camelCase fields. The SDK's TS types don't model it, hence the casts.
    // The snake_case `us_bank_account` fallback covers SETUP_SUCCESS event
    // payloads which have historically used Stripe-style snake_case.
    const usBank =
      (obj.bankAccount as Record<string, unknown> | undefined) ??
      (obj.us_bank_account as Record<string, unknown> | undefined) ??
      ((obj.payment_method_details as Record<string, unknown> | undefined)?.us_bank_account as
        | Record<string, unknown>
        | undefined);
    const last4 = typeof usBank?.last4 === 'string' ? usBank.last4 : '';
    const bankNameRaw = usBank?.bankName ?? usBank?.bank_name;
    const bankName = typeof bankNameRaw === 'string' ? bankNameRaw : '';
    const rawAccountType = usBank?.accountType ?? usBank?.account_type;
    const accountType = rawAccountType === 'savings' ? 'savings' : 'checking';
    const accountHolderNameRaw = usBank?.accountHolderName ?? usBank?.account_holder_name;
    const accountHolderName = typeof accountHolderNameRaw === 'string' ? accountHolderNameRaw : null;
    return { id, last4, bankName, accountType, accountHolderName };
  }
  return null;
}

function pickBankTokenId(obj: Record<string, unknown>): string | null {
  const candidates: unknown[] = [
    obj.sourceId,
    obj.id,
    (obj.paymentMethod as Record<string, unknown> | undefined)?.id,
    (obj.source as Record<string, unknown> | undefined)?.id,
  ];
  for (const c of candidates) {
    if (typeof c === 'string' && c.startsWith('pm_us_bank_account_')) return c;
  }
  return null;
}

function extractFailureMessage(args: unknown[]): string | null {
  for (const arg of args) {
    if (!arg) continue;
    if (typeof arg === 'string') return arg;
    if (typeof arg === 'object') {
      const obj = arg as Record<string, unknown>;
      if (typeof obj.message === 'string') return obj.message;
      if (typeof obj.reason === 'string') return obj.reason;
    }
  }
  return null;
}
